import React, { useMemo } from 'react';
import { CheckCircle, AlertTriangle, Layers, Zap, BarChart3 } from 'lucide-react';
import { validateSeedCoverage, validateSeedQuestionsLocal, getAllSeedQuestions, getSeedQuestionTypeDistribution, SEED_COLLECTIONS, getSeedCollection, TAXONOMY, FAMILIES_BY_MODULE, MODULES, type ModuleId } from '../../../../content';
import { QUESTION_TYPE_META } from '../../../../content/questions/questionTypeRegistry';
import type { QuestionType } from '../../../../content/types';
import { getModuleHexColor } from '../../../../content/moduleColors';
export function SeedStatus() {
  const coverageReport = useMemo(() => validateSeedCoverage(), []);
  const validationReport = useMemo(() => validateSeedQuestionsLocal(), []);
  const typeDistribution = useMemo(() => getSeedQuestionTypeDistribution(), []);
  const coveragePercent = Math.round(coverageReport.coveredFamilies / coverageReport.totalFamilies * 100);
  const sortedTypes = useMemo(() => {
    return Object.entries(typeDistribution).sort((a, b) => b[1] - a[1]);
  }, [typeDistribution]);
  return <div className="space-y-6 sm:space-y-8" data-id="element-7030">
      {/* 1. Module Overzicht */}
      <div data-id="element-7031">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-7032">
          <BarChart3 className="w-5 h-5 text-blue-600" data-id="element-7033" />
          Module Overzicht
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-7034">
          {MODULES.map(module => {
          const collection = SEED_COLLECTIONS.get(module.id);
          const families = FAMILIES_BY_MODULE.get(module.id) || [];
          const questionCount = collection?.questions.length || 0;
          const familyCount = families.length;
          const moduleFamilyIds = families.map(f => f.familyId);
          const coveredModuleFamilies = new Set(collection?.questions.map(q => q.familyId) || []);
          const validCoveredCount = moduleFamilyIds.filter(id => coveredModuleFamilies.has(id)).length;
          const coverage = familyCount > 0 ? Math.round(validCoveredCount / familyCount * 100) : 0;
          const tiers = collection?.questions.map(q => q.tier).filter(Boolean) || [];
          const minTier = tiers.length > 0 ? Math.min(...tiers) : 0;
          const maxTier = tiers.length > 0 ? Math.max(...tiers) : 0;
          return <div key={module.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col" style={{
            borderLeftWidth: '4px',
            borderLeftColor: getModuleColor(module.id)
          }} data-id="element-7035">
                <div className="p-4 flex-1" data-id="element-7036">
                  <div className="flex justify-between items-start mb-4" data-id="element-7037">
                    <h4 className="font-bold text-gray-900" data-id="element-7038">{module.title}</h4>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-md" data-id="element-7039">
                      {collection?.moduleCode || module.id.substring(0, 3).toUpperCase()}
                    </span>
                  </div>

                  <div className="space-y-2" data-id="element-7040">
                    <div className="flex justify-between text-sm" data-id="element-7041">
                      <span className="text-gray-500" data-id="element-7042">Vragen</span>
                      <span className="font-mono font-medium text-gray-900" data-id="element-7043">
                        {questionCount}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-7044">
                      <span className="text-gray-500" data-id="element-7045">Families</span>
                      <span className="font-mono font-medium text-gray-900" data-id="element-7046">
                        {familyCount}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-7047">
                      <span className="text-gray-500" data-id="element-7048">Tier bereik</span>
                      <span className="font-mono font-medium text-gray-900" data-id="element-7049">
                        {minTier > 0 ? `${minTier}-${maxTier}` : '-'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="px-4 py-3 bg-gray-50 border-t border-gray-100" data-id="element-7050">
                  <div className="flex justify-between text-xs mb-1.5" data-id="element-7051">
                    <span className="text-gray-500 font-medium" data-id="element-7052">Dekking</span>
                    <span className={`font-bold ${coverage === 100 ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-7053">
                      {coverage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5" data-id="element-7054">
                    <div className={`h-1.5 rounded-full ${coverage === 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{
                  width: `${coverage}%`
                }} data-id="element-7055" />
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-7056">
        {/* 2. Dekkingsstatus */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-7057">
          <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-7058">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-7059">
              <CheckCircle className="w-4 h-4 text-emerald-600" data-id="element-7060" />
              Dekkingsstatus
            </h3>
          </div>
          <div className="p-4 sm:p-5" data-id="element-7061">
            <div className="mb-6" data-id="element-7062">
              <div className="flex justify-between items-end mb-2" data-id="element-7063">
                <span className="text-sm font-medium text-gray-600" data-id="element-7064">
                  Totale dekking
                </span>
                <span className={`text-2xl font-bold ${coveragePercent === 100 ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-7065">
                  {coveragePercent}%
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden" data-id="element-7066">
                <div className={`h-full transition-all duration-500 ${coveragePercent === 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{
                width: `${coveragePercent}%`
              }} data-id="element-7067" />
              </div>
            </div>

            {coverageReport.missingFamilies.length > 0 ? <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-id="element-7068">
                <h4 className="text-sm font-bold text-amber-800 mb-3 flex items-center gap-2" data-id="element-7069">
                  <AlertTriangle className="w-4 h-4" data-id="element-7070" />
                  Ontbrekende families ({coverageReport.missingFamilies.length})
                </h4>
                <div className="space-y-2 max-h-60 overflow-y-auto pr-2" data-id="element-7071">
                  {coverageReport.missingFamilies.map(id => <div key={id} className="flex items-center justify-between bg-white p-2 rounded border border-amber-100 text-sm" data-id="element-7072">
                      <span className="font-mono text-amber-900" data-id="element-7073">{id}</span>
                    </div>)}
                </div>
              </div> : <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3" data-id="element-7074">
                <div className="bg-emerald-100 p-2 rounded-full flex-shrink-0" data-id="element-7075">
                  <CheckCircle className="w-5 h-5 text-emerald-600" data-id="element-7076" />
                </div>
                <div data-id="element-7077">
                  <div className="font-bold text-emerald-900" data-id="element-7078">
                    Alle families gedekt
                  </div>
                  <div className="text-sm text-emerald-700" data-id="element-7079">
                    Alle {coverageReport.totalFamilies} families uit de
                    taxonomie hebben minimaal één seed vraag.
                  </div>
                </div>
              </div>}
          </div>
        </div>

        {/* 3. Vraagtypen */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-7080">
          <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-7081">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-7082">
              <Layers className="w-4 h-4 text-purple-600" data-id="element-7083" />
              Vraagtypen
            </h3>
          </div>
          <div className="p-4 sm:p-5" data-id="element-7084">
            <div className="space-y-3" data-id="element-7085">
              {sortedTypes.map(([type, count]) => <div key={type} className="flex items-center justify-between" data-id="element-7086">
                  <div className="flex items-center gap-2" data-id="element-7087">
                    <CheckCircle className="w-4 h-4 text-emerald-500" data-id="element-7088" />
                    <span className="text-sm font-medium text-gray-700" data-id="element-7089">
                      {translateQuestionType(type)}
                    </span>
                  </div>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full" data-id="element-7090">
                    {count}
                  </span>
                </div>)}
              {sortedTypes.length === 0 && <div className="text-sm text-gray-500 text-center py-4" data-id="element-7091">
                  Geen vraagtypen gevonden
                </div>}
            </div>
          </div>
        </div>
      </div>

      {/* 4. Validatie */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-7092">
        <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center" data-id="element-7093">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-7094">
            <Zap className="w-4 h-4 text-amber-500" data-id="element-7095" />
            Validatie Resultaten
          </h3>
          <div className="flex gap-2 text-xs font-medium" data-id="element-7096">
            <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full" data-id="element-7097">
              {validationReport.valid} Geldig
            </span>
            {validationReport.invalid > 0 && <span className="px-2.5 py-1 bg-rose-100 text-rose-700 rounded-full" data-id="element-7098">
                {validationReport.invalid} Ongeldig
              </span>}
          </div>
        </div>
        <div className="p-4 sm:p-5" data-id="element-7099">
          {validationReport.issues.length > 0 ? <div className="space-y-3" data-id="element-7100">
              {validationReport.issues.map(issue => <div key={issue.id} className="bg-rose-50 border border-rose-200 rounded-lg p-3 sm:p-4" data-id="element-7101">
                  <div className="font-mono text-sm font-bold text-rose-800 mb-2" data-id="element-7102">
                    {issue.id}
                  </div>
                  <ul className="list-disc list-inside text-sm text-rose-700 space-y-1" data-id="element-7103">
                    {issue.problems.map((p, i) => <li key={i} data-id="element-7104">{p}</li>)}
                  </ul>
                </div>)}
            </div> : <div className="text-center py-8" data-id="element-7105">
              <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-3" data-id="element-7106" />
              <p className="text-gray-600 font-medium" data-id="element-7107">
                Alle seed vragen zijn succesvol gevalideerd
              </p>
            </div>}
        </div>
      </div>
    </div>;
}
// Helpers
function getModuleColor(moduleId: ModuleId): string {
  const mod = MODULES.find(m => m.id === moduleId);
  if (!mod) return '#64748b';
  return getModuleHexColor(mod.color);
}
// F-A4 (19 May 2026): use QUESTION_TYPE_META as the SSOT for Dutch labels
// instead of a hand-maintained local map. The old map covered only 14 of
// the 38 registered types — anything outside that set rendered raw English
// keys (e.g. `text_highlight`, `verb_conjugation_flow`) and drifted out of
// sync as new types were added. Audit §19.2.4 / §20.1.
function translateQuestionType(type: string): string {
  const meta = QUESTION_TYPE_META[type as QuestionType];
  return meta?.label ?? type;
}